import { useCallback, useState } from 'react'
import { Settings as SettingsIcon } from 'lucide-react'
import { DiagnosticsDialog } from '@/app/diagnostics/DiagnosticsDialog'
import {
  SETTINGS_ITEMS,
  SettingsItem,
  THEME_LABELS,
  THEME_MODES,
} from '@/app/settings/settings'
import { useEditSession } from '@/core/store/editSession'
import { TEST_IDS } from '@/lib/testIds'
import { AppVersion } from '@/ui/AppVersion'
import { ContactLinks } from '@/ui/ContactLinks'
import { PersistenceStatusIndicator } from '@/ui/PersistenceStatusIndicator'
import { RecentEditsDialog } from '@/ui/RecentEditsDialog'
import { WhatsNewDialog } from '@/app/whatsNew/WhatsNewDialog'
import { Button } from '@/ui/shadcn/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/ui/shadcn/dialog'
import { Popover, PopoverContent, PopoverTrigger } from '@/ui/shadcn/popover'

interface SettingsContentProps {
  mobile: boolean
  openDiagnostics: () => void
  openRecents: () => void
  openWhatsNew: () => void
}

function SettingsContent({ mobile, openDiagnostics, openRecents, openWhatsNew }: SettingsContentProps) {
  const themeMode = useEditSession((state) => state.themeMode)
  const setThemeMode = useEditSession((state) => state.setThemeMode)

  const renderItem = (item: SettingsItem) => {
    switch (item) {
      case SettingsItem.Theme:
        return (
          <div className="space-y-3">
            <p className="text-sm font-medium">Theme</p>
            <div className="grid grid-cols-3 gap-1 rounded-lg bg-muted p-1" aria-label="Theme">
              {THEME_MODES.map((mode) => (
                <Button
                  key={mode}
                  type="button"
                  variant={themeMode === mode ? 'secondary' : 'ghost'}
                  size="sm"
                  aria-pressed={themeMode === mode}
                  aria-label={`Set theme to ${THEME_LABELS[mode]}`}
                  data-testid={TEST_IDS.settingsThemeMode(mode)}
                  onClick={() => setThemeMode(mode)}
                >
                  {THEME_LABELS[mode]}
                </Button>
              ))}
            </div>
          </div>
        )
      case SettingsItem.RecentEdits:
        return (
          <Button
            type="button"
            variant="ghost"
            className="h-auto w-full justify-between px-0 py-1 text-left"
            data-testid={TEST_IDS.openRecent}
            onClick={openRecents}
          >
            <span>Recent edits</span>
            <PersistenceStatusIndicator />
          </Button>
        )
      case SettingsItem.Diagnostics:
        return (
          <Button
            type="button"
            variant="ghost"
            className="h-auto w-full justify-start px-0 py-1"
            onClick={openDiagnostics}
          >
            Diagnostics
          </Button>
        )
      case SettingsItem.WhatsNew:
        return (
          <Button
            type="button"
            variant="ghost"
            className="h-auto w-full justify-start px-0 py-1"
            data-testid={TEST_IDS.whatsNewOpen}
            onClick={openWhatsNew}
          >
            What's new
          </Button>
        )
      case SettingsItem.Contact:
        return (
          <div className="space-y-2">
            <p className="text-sm font-medium">Contact</p>
            <ContactLinks showFooter={false} className="justify-start" />
          </div>
        )
      default:
        throw new Error(`Unknown Settings item: "${String(item)}"`)
    }
  }

  return (
    <div
      data-testid={TEST_IDS.settingsPanel}
      data-presentation={mobile ? 'sheet' : 'popover'}
      className="space-y-4"
    >
      <ul className="divide-y">
        {SETTINGS_ITEMS.map((item) => (
          <li key={item} className="py-4 first:pt-0 last:pb-0">{renderItem(item)}</li>
        ))}
      </ul>
      <footer className="flex items-center justify-between border-t pt-3 text-xs text-muted-foreground">
        <a
          href="/privacy.html"
          data-testid={TEST_IDS.privacyLink}
          className="rounded-sm underline-offset-4 hover:text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          Privacy
        </a>
        <button
          type="button"
          className="rounded-sm underline-offset-4 hover:text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          aria-label="Open What's new"
          onClick={openWhatsNew}
        >
          <AppVersion includeCommit />
        </button>
      </footer>
    </div>
  )
}

export function SettingsSheet({ mobile }: { mobile: boolean }) {
  const settingsOpen = useEditSession((state) => state.settingsOpen)
  const setSettingsOpen = useEditSession((state) => state.setSettingsOpen)
  const recentsOpen = useEditSession((state) => state.recentsOpen)
  const setRecentsOpen = useEditSession((state) => state.setRecentsOpen)
  const [diagnosticsOpen, setDiagnosticsOpen] = useState(false)
  const [whatsNewOpen, setWhatsNewOpen] = useState(false)

  const openRecents = () => {
    setSettingsOpen(false)
    setRecentsOpen(true)
  }
  const setDiagnosticsVisibility = useCallback((open: boolean) => {
    if (open) setSettingsOpen(false)
    setDiagnosticsOpen(open)
  }, [setSettingsOpen])
  const openWhatsNew = () => {
    setSettingsOpen(false)
    setWhatsNewOpen(true)
  }

  const trigger = (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      aria-label="Open Settings"
      data-testid={TEST_IDS.settingsOpen}
    >
      <SettingsIcon aria-hidden="true" />
    </Button>
  )
  const content = (
    <SettingsContent
      mobile={mobile}
      openDiagnostics={() => setDiagnosticsVisibility(true)}
      openRecents={openRecents}
      openWhatsNew={openWhatsNew}
    />
  )

  return (
    <>
      {mobile ? (
        <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
          <DialogTrigger asChild>{trigger}</DialogTrigger>
          <DialogContent className="bottom-0 left-0 top-auto max-h-[85dvh] w-full max-w-none translate-x-0 translate-y-0 overflow-y-auto rounded-b-none rounded-t-2xl">
            <DialogHeader>
              <DialogTitle>Settings</DialogTitle>
              <DialogDescription>App preferences, saved edits, support, and device information.</DialogDescription>
            </DialogHeader>
            {content}
          </DialogContent>
        </Dialog>
      ) : (
        <Popover open={settingsOpen} onOpenChange={setSettingsOpen}>
          <PopoverTrigger asChild>{trigger}</PopoverTrigger>
          <PopoverContent align="end" className="w-96">
            <h2 className="mb-1 font-semibold">Settings</h2>
            <p className="mb-4 text-sm text-muted-foreground">Preferences, saved edits, and support.</p>
            {content}
          </PopoverContent>
        </Popover>
      )}
      <RecentEditsDialog mobile={mobile} open={recentsOpen} onOpenChange={setRecentsOpen} />
      <DiagnosticsDialog open={diagnosticsOpen} onOpenChange={setDiagnosticsVisibility} />
      <WhatsNewDialog mobile={mobile} open={whatsNewOpen} onOpenChange={setWhatsNewOpen} />
    </>
  )
}
